import type { GridApi, MenuItemDef, ValueFormatterParams } from "ag-grid-community";

export const GRID_DISPLAY_DECIMALS = [0, 1, 2, 3, 4, 5, 6] as const;
export const GRID_DISPLAY_ROW_HEIGHTS = [20, 24, 28, 32, 36] as const;
export const GRID_DISPLAY_ROW_HEIGHTS_COMPACT = [18, 20, 22, 24, 28] as const;
export const GRID_DISPLAY_FONT_SIZES = [10, 11, 12, 13, 14] as const;
export const GRID_DISPLAY_FONT_SIZES_COMPACT = [9, 10, 11, 12, 13] as const;

export type GridDisplayPrefs = {
  decimals: number;
  rowHeight: number;
  fontSize: number;
};

export const GRID_DISPLAY_DEFAULTS: GridDisplayPrefs = {
  decimals: 3,
  rowHeight: 28,
  fontSize: 12,
};

export const CORRELATION_PAIRS_DISPLAY_DEFAULTS: GridDisplayPrefs = {
  decimals: 4,
  rowHeight: 22,
  fontSize: 10,
};

export const MO_BOOKING_ISSUES_DISPLAY_DEFAULTS: GridDisplayPrefs = {
  decimals: 3,
  rowHeight: 24,
  fontSize: 12,
};

export const MODULE_DISPLAY_DEFAULTS: Record<string, GridDisplayPrefs> = {
  "correlation-pairs": CORRELATION_PAIRS_DISPLAY_DEFAULTS,
  "mo-booking-issues": MO_BOOKING_ISSUES_DISPLAY_DEFAULTS,
  "curves-desk": { decimals: 4, rowHeight: 20, fontSize: 11 },
};

export function createNumericValueFormatter(decimals: number) {
  return (p: ValueFormatterParams) => {
    if (p.value == null || p.value === "") return "";
    const n = Number(p.value);
    if (!Number.isFinite(n)) return String(p.value);
    return n.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
}

export function applyNumericColumnFormatters<T>(
  cols: import("ag-grid-community").ColDef<T>[],
  formatter: (p: ValueFormatterParams) => string,
): import("ag-grid-community").ColDef<T>[] {
  return cols.map((col) => {
    if (col.type !== "numericColumn" && col.valueFormatter) return col;
    if (col.type === "numericColumn" || col.field === "price" || col.field === "quantity") {
      return { ...col, valueFormatter: formatter };
    }
    if (
      col.field === "qty" ||
      col.field === "filled_qty" ||
      col.field === "remaining_qty" ||
      col.field === "limit_price" ||
      col.field === "avg_fill_price" ||
      col.field === "notional"
    ) {
      return { ...col, valueFormatter: formatter };
    }
    return col;
  });
}

type DisplaySetters = {
  setDecimals: (v: number) => void;
  setRowHeight: (v: number) => void;
  setFontSize: (v: number) => void;
};

type DisplayMenuOptions = {
  rowHeights?: readonly number[];
  fontSizes?: readonly number[];
};

/** Right-click submenu block: decimals, row height, font size (matches positions grid). */
export function buildGridDisplayMenuItems(
  api: GridApi,
  display: GridDisplayPrefs,
  setters: DisplaySetters,
  options?: DisplayMenuOptions,
): MenuItemDef[] {
  const { decimals, rowHeight, fontSize } = display;
  const { setDecimals, setRowHeight, setFontSize } = setters;
  const rowHeights = options?.rowHeights ?? GRID_DISPLAY_ROW_HEIGHTS;
  const fontSizes = options?.fontSizes ?? GRID_DISPLAY_FONT_SIZES;

  const decimalSubItems: MenuItemDef[] = GRID_DISPLAY_DECIMALS.map((d) => ({
    name: `${d} decimals`,
    checked: decimals === d,
    action: () => {
      setDecimals(d);
      api.refreshCells({ force: true });
    },
    icon: decimals === d ? "✓" : "",
  }));

  const rowHeightSubItems: MenuItemDef[] = rowHeights.map((h) => ({
    name: `${h}px`,
    checked: rowHeight === h,
    action: () => {
      setRowHeight(h);
      api.resetRowHeights();
    },
    icon: rowHeight === h ? "✓" : "",
  }));

  const fontSizeSubItems: MenuItemDef[] = fontSizes.map((s) => ({
    name: `${s}px`,
    checked: fontSize === s,
    action: () => setFontSize(s),
    icon: fontSize === s ? "✓" : "",
  }));

  return [
    { name: "Decimals", subMenu: decimalSubItems },
    { name: "Row height", subMenu: rowHeightSubItems },
    { name: "Font size", subMenu: fontSizeSubItems },
  ];
}
